import { Empty, Flex, Skeleton, Typography } from 'antd';
import { useId } from 'react';
import WidgetCard, { ActivityWidgetProps, WidgetTitle } from '../WidgetCard';

import { formatNumberWithComma, renderInfoValue } from 'utils/helpers';

import { CashActivity } from 'screens/collections/hotcash/types';
import DateTime from 'utils/datetime';

const { Text } = Typography;

interface AgronomistActivityWidgetInfoProps {
  type: 'success' | 'info';
  agronomistName: string;
  farmerName: string;
  timestamp: string;
  amount: number;
}

const AgronomistActivityWidgetInfo = ({
  agronomistName,
  farmerName,
  timestamp,
  type,
  amount,
}: AgronomistActivityWidgetInfoProps) => {
  return (
    <Flex justify="space-between" align="flex-start">
      <Flex vertical gap={6} align="flex-start" wrap="wrap">
        <Flex gap={10} align="center" wrap="nowrap">
          <WidgetTitle type="normal" style={{ maxWidth: '75%' }}>
            {renderInfoValue(agronomistName)}
          </WidgetTitle>
          <Text ellipsis type="secondary" className="activity-time-stamp">
            {renderInfoValue(DateTime.fromNow(timestamp, 'N/A'))}
          </Text>
        </Flex>
        <Text className="m-0" ellipsis style={{ maxWidth: '200px' }}>
          {' '}
          Farmer {renderInfoValue(farmerName)}
        </Text>
      </Flex>
      <Flex
        vertical
        gap={6}
        align="flex-end"
        className="todays-activity-widget--info"
      >
        <WidgetTitle type="normal" variant={type}>
          {type === 'success' ? 'Collected' : 'Cash In'}
        </WidgetTitle>
        <WidgetTitle
          type="bold"
          className="m-0"
          variant={type}
          style={{ maxWidth: '200px' }}
        >
          {amount > 0 && (
            <>
              {type === 'success' ? '-' : '+'}
              {''}
              {renderInfoValue(formatNumberWithComma(amount))}
            </>
          )}
        </WidgetTitle>
      </Flex>
    </Flex>
  );
};

export const TodaysActivityWidgetSkeleton = () => {
  return (
    <WidgetCard className="activity-widget activity-widget--todays-activity">
      <Skeleton active paragraph={{ rows: 0 }} />
      <Flex justify={'space-between'}>
        <Skeleton active paragraph={{ rows: 1 }} />
        <Skeleton active paragraph={{ rows: 1 }} />
      </Flex>
      <Flex justify={'space-between'}>
        <Skeleton active paragraph={{ rows: 1 }} />
        <Skeleton active paragraph={{ rows: 1 }} />
      </Flex>
    </WidgetCard>
  );
};

interface TodaysActivityWidgetProps extends ActivityWidgetProps {
  activityList: CashActivity[];
}

const TodaysActivityWidget = ({
  isLoading,
  activityList,
}: TodaysActivityWidgetProps) => {
  if (isLoading) return <TodaysActivityWidgetSkeleton />;

  return (
    <WidgetCard
      style={{ height: '100%' }}
      className="activity-widget activity-widget--todays-activity"
    >
      <WidgetTitle className="mb-2 sticky-title" type="bold">
        {activityList?.length ? "Today's Activities" : ''}
      </WidgetTitle>
      <Flex vertical gap={8} className="activity-container">
        {!activityList?.length && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No activities"
          />
        )}
        {activityList?.map((activity) => (
          <AgronomistActivityWidgetInfo
            key={useId()}
            agronomistName={activity.agName}
            farmerName={activity.farmerName}
            timestamp={
              activity.status === 'DEPOSITED'
                ? activity.depositedAt
                : activity.receivedAt
            }
            type={activity.status === 'DEPOSITED' ? 'success' : 'info'}
            amount={activity.cashCollected}
          />
        ))}
      </Flex>
    </WidgetCard>
  );
};

export default TodaysActivityWidget;
